import { useEffect, useMemo, useState } from "react";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { useIsMobile } from "../../lib/useIsMobile";
import { ManualReceiptDetailDrawer } from "./ManualReceiptDetailDrawer";

/** Manuel mal kabul fişleri sekmesi — liste (masaüstü tablo / mobil kart) + detay drawer. */
export function ManualReceiptsSection() {
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState<string | null>(null);
  const pageSize = 50;

  useEffect(() => { const t = setTimeout(() => { setQDebounced(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);

  const listQ = trpc.inventory.manualReceipts.useQuery({ q: qDebounced || undefined, page, pageSize }, { staleTime: 10_000 });
  const rows = listQ.data?.rows ?? [];
  const total = listQ.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const fmtDate = (iso: string) => new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });

  const search = useMemo(() => (
    <div style={{ position: "relative", flex: "1 1 260px", minWidth: 200 }}>
      <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--neutral-400)", pointerEvents: "none" }}><Icon name="search" size={15} /></span>
      <input className="form-input" style={{ paddingLeft: 32, width: "100%" }} placeholder="Fiş no, teslim eden, tedarikçi ara…" value={q} onChange={(e) => setQ(e.target.value)} type="search" aria-label="Mal kabul ara" />
    </div>
  ), [q]);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        {search}
        <span style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{total} fiş</span>
      </div>

      {listQ.isLoading ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : listQ.isError ? (
        <div className="empty-state"><p className="ttl" style={{ color: "var(--danger)" }}>Yüklenemedi</p><p className="sub">{listQ.error?.message}</p></div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p className="ttl">{qDebounced ? "Fiş bulunamadı" : "Henüz manuel mal kabul yok"}</p>
          <p className="sub">{qDebounced ? "Arama terimini değiştirin." : 'Üstteki "Mal Kabul" ile ilk fişi oluşturun.'}</p>
        </div>
      ) : isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((r) => (
            <button key={r.id} type="button" className="m-card m-card-p" style={{ textAlign: "left", cursor: "pointer" }} onClick={() => setDetailId(r.id)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span className="mono" style={{ fontWeight: 700, fontSize: 13 }}>{r.code}</span>
                <span style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>{fmtDate(r.receivedAt)}</span>
              </div>
              <div style={{ marginTop: 6, fontSize: 13 }}>{r.deliveredByName || <span style={{ color: "var(--neutral-400)" }}>Teslim eden belirtilmemiş</span>}</div>
              <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: "4px 8px", fontSize: 11.5, color: "var(--neutral-500)" }}>
                <span className="badge-pill b-info"><Icon name="package" size={10} />{r.lineCount} kalem</span>
                {r.supplierName ? <span>· {r.supplierName}</span> : null}
                {r.attachmentCount > 0 ? <span className="badge-pill"><Icon name="fileText" size={10} />{r.attachmentCount}</span> : null}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="dt2-wrap">
          <div className="dt2-scroll">
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Fiş No</th>
                  <th style={{ width: 150 }}>Tarih</th>
                  <th>Teslim Eden</th>
                  <th>Tedarikçi</th>
                  <th className="num" style={{ width: 80 }}>Kalem</th>
                  <th className="num" style={{ width: 70 }}>Ek</th>
                  <th>Teslim Alan</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => setDetailId(r.id)}>
                    <td><span className="mono" style={{ fontWeight: 600 }}>{r.code}</span></td>
                    <td>{fmtDate(r.receivedAt)}</td>
                    <td>
                      {r.deliveredByName || <span style={{ color: "var(--neutral-400)" }}>—</span>}
                      {r.deliveredByPhone ? <span className="mono" style={{ display: "block", fontSize: 10.5, color: "var(--neutral-400)" }}>{r.deliveredByPhone}</span> : null}
                    </td>
                    <td>{r.supplierName ?? <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                    <td className="cell-num">{r.lineCount}</td>
                    <td className="cell-num">{r.attachmentCount > 0 ? r.attachmentCount : <span style={{ color: "var(--neutral-300)" }}>—</span>}</td>
                    <td>{r.receivedBy ?? <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="dt2-foot" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>{total} fiş</span>
            {pages > 1 ? (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button type="button" className="btn" style={{ height: 28 }} disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Önceki sayfa"><Icon name="chevLeft" size={14} /></button>
                <span className="mono" style={{ fontSize: 12 }}>{page} / {pages}</span>
                <button type="button" className="btn" style={{ height: 28 }} disabled={page >= pages} onClick={() => setPage((p) => p + 1)} aria-label="Sonraki sayfa"><Icon name="chevRight" size={14} /></button>
              </div>
            ) : null}
          </div>
        </div>
      )}

      <ManualReceiptDetailDrawer open={!!detailId} receiptId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
